// src/components/PortalAfiliado/RenovacaoAnual.tsx
// UPDATED: 2026-03-27
// ✅ Dados reais da API — data de entrada do filiado + renovação
// ✅ Contagem exata de dias/meses restantes

import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar, DollarSign, CreditCard, CheckCircle,
  Clock, AlertTriangle, HelpCircle, Sparkles, Loader2,
  RefreshCw, Building2,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import apiClient from '../../services/api';

interface RootState {
  auth: { user?: { affiliateId?: string } };
}

interface AffiliateInfo {
  id: string;
  schoolName: string;
  status: string;
  approvedAt: string | null;
  renewalDate: string | null;
  lastRenewalDate: string | null;
  createdAt: string;
}

interface Renewal {
  id: string;
  renewalYear: number;
  amount: number;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  dueDate: string;
  paidAt: string | null;
  createdAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d?: string | null, opts?: Intl.DateTimeFormatOptions) =>
  d ? new Date(d).toLocaleDateString('pt-BR', opts ?? { day: '2-digit', month: 'long', year: 'numeric' }) : '—';

const calcDiasRestantes = (dataVenc?: string | null): number | null => {
  if (!dataVenc) return null;
  return Math.ceil((new Date(dataVenc).getTime() - Date.now()) / 86400000);
};

const formatTempo = (dias: number): string => {
  if (dias < 0)   return `Venceu há ${Math.abs(dias)} dia${Math.abs(dias) !== 1 ? 's' : ''}`;
  if (dias === 0) return 'Vence hoje';
  if (dias < 30)  return `${dias} dia${dias !== 1 ? 's' : ''} restante${dias !== 1 ? 's' : ''}`;
  const meses = Math.floor(dias / 30);
  const diasR = dias % 30;
  if (diasR === 0) return `${meses} ${meses === 1 ? 'mês' : 'meses'} restante${meses !== 1 ? 's' : ''}`;
  return `${meses} ${meses === 1 ? 'mês' : 'meses'} e ${diasR} dia${diasR !== 1 ? 's' : ''} restante${diasR !== 1 ? 's' : ''}`;
};

const calcTempoNaFederacao = (dataEntrada?: string | null): string => {
  if (!dataEntrada) return '—';
  const diff = Date.now() - new Date(dataEntrada).getTime();
  const dias  = Math.floor(diff / 86400000);
  const anos  = Math.floor(dias / 365);
  const meses = Math.floor((dias % 365) / 30);
  if (anos === 0 && meses === 0) return `${dias} dia${dias !== 1 ? 's' : ''}`;
  if (anos === 0) return `${meses} ${meses === 1 ? 'mês' : 'meses'}`;
  if (meses === 0) return `${anos} ano${anos !== 1 ? 's' : ''}`;
  return `${anos} ano${anos !== 1 ? 's' : ''} e ${meses} ${meses === 1 ? 'mês' : 'meses'}`;
};

const faqItems = [
  { q: 'O que está incluído na renovação anual?',  a: 'A renovação inclui acesso ao portal, emissão de certificados, homologação de cursos e suporte técnico por 12 meses.' },
  { q: 'Posso parcelar o valor da renovação?',     a: 'Sim! Aceitamos parcelamento em até 12x no cartão de crédito sem juros.' },
  { q: 'O que acontece se não renovar no prazo?',  a: 'Após o vencimento, o acesso ao portal fica suspenso até a regularização.' },
];

// ─── Componente ───────────────────────────────────────────────────────────────
const RenovacaoAnual: React.FC = () => {
  const affiliateId = useSelector((s: RootState) => s.auth.user?.affiliateId ?? '');

  const [affiliate, setAffiliate]   = useState<AffiliateInfo | null>(null);
  const [renewals, setRenewals]     = useState<Renewal[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [openFaq, setOpenFaq]       = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    if (!affiliateId) {
      setLoading(false);
      setError('Não foi possível identificar o filiado desta sessão. Entre novamente.');
      return;
    }
    setLoading(true); setError(null);
    try {
      const [affRes, renewRes] = await Promise.allSettled([
        apiClient.get<AffiliateInfo>(`/affiliates/${affiliateId}`),
        apiClient.get<{ renewals: Renewal[]; total: number }>('/renewals/me'),
      ]);

      if (affRes.status === 'fulfilled')   setAffiliate(affRes.value);
      if (renewRes.status === 'fulfilled') setRenewals(renewRes.value.renewals ?? []);
    } catch (err: any) {
      setError(err?.message ?? 'Erro ao carregar dados');
    } finally { setLoading(false); }
  }, [affiliateId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Dados derivados ────────────────────────────────────────────────────────
  const ultimaRenovacao = renewals.find(r => r.status === 'paid');
  const renovacaoPendente = renewals.find(r => r.status === 'pending' || r.status === 'overdue');

  // Data de vencimento: preferência para renewalDate do affiliate
  const dataVencimento = affiliate?.renewalDate ?? renovacaoPendente?.dueDate ?? null;
  const diasRestantes  = calcDiasRestantes(dataVencimento);

  // Data de entrada: approvedAt ou createdAt
  const dataEntrada    = affiliate?.approvedAt ?? affiliate?.createdAt ?? null;
  const tempoFederacao = calcTempoNaFederacao(dataEntrada);

  // Status visual
  const statusViz = !dataVencimento ? 'ativa'
    : diasRestantes === null ? 'ativa'
    : diasRestantes < 0  ? 'vencida'
    : diasRestantes < 30 ? 'vencendo'
    : 'ativa';

  const statusMeta = {
    ativa:    { label: 'Filiação Ativa',     bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', bar: '#22c55e', Icon: CheckCircle  },
    vencendo: { label: 'Vencimento Próximo', bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   bar: '#f59e0b', Icon: Clock        },
    vencida:  { label: 'Filiação Vencida',   bg: 'bg-red-50',     text: 'text-red-600',     border: 'border-red-200',     bar: '#ef4444', Icon: AlertTriangle },
  };

  const meta = statusMeta[statusViz];

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex flex-col items-center justify-center py-24 gap-3">
      <Loader2 className="w-8 h-8 text-gold-500 animate-spin"/>
      <p className="text-sm text-gray-400">Carregando dados da filiação…</p>
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center py-24 gap-4 text-center max-w-md mx-auto">
      <AlertTriangle className="w-10 h-10 text-red-400"/>
      <p className="text-sm font-medium text-gray-700">Erro ao carregar dados</p>
      <p className="text-xs text-gray-400">{error}</p>
      <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
        <RefreshCw className="w-4 h-4"/> Tentar novamente
      </button>
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl">

      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3.5">
          <div className="relative flex-shrink-0">
            <div className="absolute -inset-1 bg-gold-400/20 blur-[10px] rounded-full"/>
            <img src="/icons/renov.png" alt="Renovação"
              className="relative w-11 h-11 object-contain drop-shadow-[0_2px_10px_rgba(212,175,55,0.45)]"/>
          </div>
          <div>
            <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">Renovação Anual</h1>
            <p className="text-xs text-gray-400 mt-1">Gerencie sua filiação e renovação anual</p>
          </div>
        </div>
        <button onClick={fetchData} className="p-2 rounded-xl border border-gray-200 text-gray-400 hover:bg-gray-50">
          <RefreshCw className="w-4 h-4"/>
        </button>
      </div>

      {/* STATUS CARD */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden">
        <div className="h-1.5 w-full" style={{ background: meta.bar }}/>

        <div className="p-6 sm:p-8">

          {/* Status + badge */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-medium border ${meta.bg} ${meta.text} ${meta.border}`}>
              <meta.Icon className="w-4 h-4 flex-shrink-0"/>
              {meta.label}
            </span>
            {affiliate?.schoolName && (
              <span className="text-xs text-gray-400 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5"/> {affiliate.schoolName}
              </span>
            )}
          </div>

          {/* Grid de informações */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">

            {/* Data de entrada na federação */}
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
              <p className="text-[10.5px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Membro desde
              </p>
              <p className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-gold-500"/>
                {fmtDate(dataEntrada)}
              </p>
              <p className="text-xs text-gray-400">{tempoFederacao} na federação</p>
            </div>

            {/* Vencimento */}
            <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
              <p className="text-[10.5px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Vencimento da filiação
              </p>
              {dataVencimento ? (
                <>
                  <p className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-gray-400"/>
                    {fmtDate(dataVencimento)}
                  </p>
                  <p className={`text-xs font-medium ${diasRestantes !== null && diasRestantes < 0 ? 'text-red-500' : diasRestantes !== null && diasRestantes < 30 ? 'text-amber-600' : 'text-gray-400'}`}>
                    {diasRestantes !== null ? formatTempo(diasRestantes) : '—'}
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-400">Sem vencimento cadastrado</p>
              )}
            </div>

            {/* Última renovação */}
            {ultimaRenovacao && (
              <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                <p className="text-[10.5px] font-semibold text-emerald-600 uppercase tracking-wider mb-2">
                  Última Renovação Paga
                </p>
                <p className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-1">
                  <CheckCircle className="w-4 h-4 text-emerald-500"/>
                  {fmtDate(ultimaRenovacao.paidAt ?? ultimaRenovacao.createdAt)}
                </p>
                <p className="text-xs text-emerald-600">
                  Ano {ultimaRenovacao.renewalYear} · R$ {Number(ultimaRenovacao.amount).toFixed(2)}
                </p>
              </div>
            )}

            {/* Valor da renovação pendente */}
            {renovacaoPendente && (
              <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
                <p className="text-[10.5px] font-semibold text-amber-600 uppercase tracking-wider mb-2">
                  Renovação Pendente
                </p>
                <p className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-gold-600"/>
                  R$ {Number(renovacaoPendente.amount).toFixed(2)}
                </p>
                <p className="text-xs text-amber-600">Ano {renovacaoPendente.renewalYear}</p>
              </div>
            )}
          </div>

          {/* CTA renovar */}
          {statusViz !== 'ativa' && (
            <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-5">
              <div className="flex items-start gap-3 mb-4">
                <Sparkles className="w-4 h-4 text-gold-500 flex-shrink-0 mt-0.5"/>
                <p className="text-sm text-gray-600">
                  Clique no botão abaixo para prosseguir com o pagamento da renovação anual.
                  O processo leva apenas alguns minutos.
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium text-cosmos-900 hover:shadow-gold transition-all"
                  style={{ background: 'linear-gradient(135deg,#f4d03f,#d4af37)' }}>
                  <CreditCard className="w-4 h-4"/> Renovar Agora
                </button>
                <button className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                  Gerar Boleto
                </button>
              </div>
            </div>
          )}

          {/* Sem renovação pendente e filiação ativa */}
          {statusViz === 'ativa' && !renovacaoPendente && (
            <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0"/>
              <p className="text-sm text-emerald-700 font-medium">Sua filiação está em dia. Nenhuma ação necessária.</p>
            </div>
          )}
        </div>
      </div>

      {/* HISTÓRICO */}
      {renewals.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-400"/>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Histórico de Renovações</p>
          </div>
          <div className="divide-y divide-gray-50">
            {renewals.map(r => {
              const statusR = {
                paid:      { label: 'Pago',      bg: 'bg-emerald-100', text: 'text-emerald-700' },
                pending:   { label: 'Pendente',  bg: 'bg-amber-100',   text: 'text-amber-700'   },
                overdue:   { label: 'Vencido',   bg: 'bg-red-100',     text: 'text-red-700'     },
                cancelled: { label: 'Cancelado', bg: 'bg-gray-100',    text: 'text-gray-600'    },
              }[r.status] ?? { label: r.status, bg: 'bg-gray-100', text: 'text-gray-600' };
              return (
                <div key={r.id} className="px-6 py-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Ano {r.renewalYear}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Vencimento: {fmtDate(r.dueDate, { day: '2-digit', month: 'short', year: 'numeric' })}
                      {r.paidAt && ` · Pago em: ${fmtDate(r.paidAt, { day: '2-digit', month: 'short', year: 'numeric' })}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-sm font-semibold text-gray-800">R$ {Number(r.amount).toFixed(2)}</p>
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${statusR.bg} ${statusR.text}`}>
                      {statusR.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* FAQ */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_1px_8px_rgba(0,0,0,0.05)] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-2">
          <HelpCircle className="w-4 h-4 text-gray-400"/>
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Dúvidas Frequentes</p>
        </div>
        <div className="divide-y divide-gray-50">
          {faqItems.map((item, idx) => (
            <button key={idx} onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
              className="w-full text-left px-6 py-4 hover:bg-gray-50/60 transition-colors">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-gray-700">{item.q}</p>
                <span className={`text-gray-400 text-lg leading-none flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-45' : ''}`}>+</span>
              </div>
              {openFaq === idx && (
                <p className="text-xs text-gray-500 mt-2 leading-relaxed pr-6">{item.a}</p>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RenovacaoAnual;
